import { Component, ViewChild, AfterViewInit, OnInit, HostListener, OnDestroy } from '@angular/core';
import { User } from 'src/app/models/user.model';
import { MatPaginator } from '@angular/material/paginator';
import { startWith, switchMap, map, catchError, tap } from 'rxjs/operators';
import { UsersService } from 'src/app/services/users.service';
import { of, BehaviorSubject, merge, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { UserDialogComponent } from '../user-dialog/user-dialog.component';
import { ConfirmationService } from 'src/app/lib/services/confirmation.service';
import { ToastsService } from 'src/app/lib/services/toasts.service';
import { MatSort } from '@angular/material/sort';
import { ReportedUser, ReportReasonCode } from 'src/app/models/reported-user.model';
import { ProfileVideo } from 'src/app/models/profile-video.model';
import { faGhost, faSkullCrossbones } from '@fortawesome/free-solid-svg-icons';
import { Router } from '@angular/router';
import { Filter } from 'src/app/modules/filtering/models';
import { TimezoneService } from 'src/app/services/timezone.service';
import { likeRatioLabel, likeRatioRowClass } from 'src/app/utils/like-ratio';

@Component({
  standalone: false,
  selector: 'sa-reported-users',
  templateUrl: './reported-users.component.html',
  styleUrls: ['./reported-users.component.scss']
})
export class ReportedUsersComponent implements AfterViewInit, OnInit, OnDestroy {

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private usersService: UsersService,
    private confirmationService: ConfirmationService,
    private toastsService: ToastsService,
    public timezoneService: TimezoneService
  ) { }

  faGhost = faGhost;
  faSkullCrossbones = faSkullCrossbones;

  query = new BehaviorSubject<string>('');
  filters = new BehaviorSubject<Filter[]>(this.getFilter());

  displayedColumns: string[] = ['profile_image', 'actions', 'name', 'gender', 'media', 'country', 'login_method', 'createdAt', 'last_activity', 'active', 'subscription_tier', 'hidden_under_review', 'reported_blocked', 'like_dislike_ratio', 'given_like_dislike_ratio', 'count', 'reason'];
  displayedColumnsMobile: string[] = ['profile_image_mobile', 'actions_mobile', 'name'];
  
  data: ReportedUser[] = [];

  resultsLength = 0;
  isLoading = true;
  hasError = false;
  limit = 30;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  public isMobileLayout = false;
  public timezoneAbbr: string = 'PST';
  private timezoneSubscription: Subscription | null = null;

  @HostListener("window:resize", [])
  onResize() {
    var width = window.screen.width;
    this.isMobileLayout = width < 567;
  }

  ngOnInit() {
    if (window.screen.width < 567) { // 768px portrait
      this.isMobileLayout = true;
    }

    this.timezoneAbbr = this.timezoneService.getCurrentTimezoneAbbr();
    this.timezoneSubscription = this.timezoneService.getTimezoneConfig().subscribe(() => {
      this.timezoneAbbr = this.timezoneService.getCurrentTimezoneAbbr();
    });
  }

  ngAfterViewInit() {
    merge(this.paginator.page, this.sort.sortChange, this.query, this.filters).pipe(
      startWith({}),
      switchMap(() => {
        this.isLoading = true;
        return this.usersService.getReportedUsers(
          this.query.value,
          this.paginator.pageIndex + 1,
          this.limit,
          `${this.sort.active}:${this.sort.direction}`,
          this.filters.value,
        );
      }),
      map(usersCollection => {
        // Flip flag to show that loading has finished.
        this.isLoading = false;
        this.hasError = false;
        this.resultsLength = usersCollection.totalCount;

        return usersCollection.results;
      }),
      catchError(() => {
        this.isLoading = false;
        this.hasError = true;
        return of([]);
      })
    ).subscribe(data => this.data = data);
  }

  // Same ratio rules as /users — the counts ride along on the reported row.
  likeRatioLabel(row): string {
    return likeRatioLabel(row, 'received');
  }

  givenLikeRatioLabel(row): string {
    return likeRatioLabel(row, 'given');
  }

  likeRatioRowClass(row): string {
    return likeRatioRowClass(row);
  }

  getFilter(): Filter[] {
    return [];
  }

  /**
   * The reported profile's playable videos. Rows here wrap the user, so the
   * table cannot reach `row.profile_videos` the way /users does.
   */
  videosFor(row: ReportedUser): ProfileVideo[] {
    return (row && row.user && row.user.profile_videos) || [];
  }

  /** Name for the video player's title bar. */
  rowTitle(row: ReportedUser): string {
    const user = row && row.user;
    if (!user) return '';
    return `${user.name || ''} ${user.lastname || ''}`.trim() || `#${user.user_id}`;
  }

  /**
   * Human label for the structured reason. Rows predating reason_code (and
   * auto-moderator rows) fall back to the legacy free-text column, so an old
   * report is still readable rather than showing as blank.
   */
  reasonLabel(row: ReportedUser): string {
    const labels: Record<ReportReasonCode, string> = {
      fake_or_spam: 'Fake / spam',
      inappropriate: 'Inappropriate',
      harassment: 'Harassment',
      underage: 'Underage',
      other: 'Other',
    };

    if (row.reason_code && labels[row.reason_code]) {
      return labels[row.reason_code];
    }
    return row.reason ? 'Legacy' : 'Unspecified';
  }

  /**
   * The reporter's own words. Prefers reason_text; falls back to the legacy
   * `reason` column, but not when that column merely repeats the code's label
   * (which is what the API writes when a client sends no free text).
   */
  reasonDetail(row: ReportedUser): string {
    const text = (row.reason_text || '').trim();
    if (text) return text;

    const legacy = (row.reason || '').trim();
    if (!legacy) return '';
    if (row.reason_code && legacy.toLowerCase() === row.reason_code) return '';
    if (legacy.toLowerCase() === this.reasonLabel(row).toLowerCase()) return '';
    return legacy;
  }

  queryChanged(e) {
    this.paginator.pageIndex = 0;
    this.query.next(e.target.value);
  }

  pageChanged(e) {
    this.paginator.pageIndex = +e.target.value - 1;
    this.query.next(this.query.value);
  }

  onFiltersUpdated(filters) {
    this.filters.next(filters);
  }

  userClicked(reportedUser: ReportedUser) {
    const dialogRef = this.dialog.open(UserDialogComponent, {
      data: {
        user: reportedUser.user,
        reportedUser: reportedUser,
      },
      minWidth: 400,
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log(`Dialog result: ${result}`);

      if (result === 'edit') {
        this.router.navigate(['/users', reportedUser.user.user_id]);
      }
    });
  }

  userBanClicked(user: User) {
    this.usersService.banUser(user).pipe(
      tap((result) => {
        this.toastsService.showSuccess('Banned user successfully.');
        this.query.next(this.query.value);
      }),
      catchError((error) => {
        this.toastsService.showDanger('Error banning user: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userUnBanClicked(user: User) {
    this.confirmationService.askForConfirmation(`Unban ${user.name} ${user.lastname}?`, `Are you sure you want to unban ${user.name} ${user.lastname}?`)
      .then(() => {
        this.usersService.unBanUser(user).pipe(
          tap((result) => {
            this.toastsService.showSuccess('Unbanned user successfully.');
            this.query.next(this.query.value);
          }),
          catchError((error) => {
            this.toastsService.showDanger('Error unbanning user: ' + JSON.stringify(error));
            return of([]);
          })
        ).subscribe();
      })
      .catch(() => {});
  }

  userPutUnderReviewClicked(user) {
    this.usersService.putUserUnderReview(user).pipe(
      tap((result) => {
        this.toastsService.showSuccess('Put user under review successfully.');
        this.query.next(this.query.value);
      }),
      catchError((error) => {
        this.toastsService.showDanger('Error putting user under review: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userRemoveFromReviewClicked(user) {
    this.confirmationService.askForConfirmation(`Remove ${user.name} ${user.lastname} from review?`, `Are you sure you want to remove ${user.name} ${user.lastname} from review?`)
      .then(() => {
        this.usersService.removeUserFromReview(user).pipe(
          tap((result) => {
            this.toastsService.showSuccess('Removed user from review successfully.');
            this.query.next(this.query.value);
          }),
          catchError((error) => {
            this.toastsService.showDanger('Error removing user from review: ' + JSON.stringify(error));
            return of([]);
          })
        ).subscribe();
      })
      .catch(() => {});
  }

  userSilentHideClicked(user: User) {
    this.usersService.silentHideUser(user).pipe(
      tap((result) => {
        this.toastsService.showSuccess('Silent hid user successfully.');
        this.query.next(this.query.value);
      }),
      catchError((error) => {
        this.toastsService.showDanger('Error silent hiding user: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userSilentUnhideClicked(user: User) {
    this.confirmationService.askForConfirmation(`Unhide ${user.name} ${user.lastname}?`, `Are you sure you want to unhide ${user.name} ${user.lastname}?`)
      .then(() => {
        this.usersService.silentUnhideUser(user).pipe(
          tap((result) => {
            this.toastsService.showSuccess('Unhid user successfully.');
            this.query.next(this.query.value);
          }),
          catchError((error) => {
            this.toastsService.showDanger('Error unhiding user: ' + JSON.stringify(error));
            return of([]);
          })
        ).subscribe();
      })
      .catch(() => {});
  }

  userBoostClicked(user: User) {
    this.usersService.boostUser(user).pipe(
      tap((result) => {
        this.toastsService.showSuccess('Boosted user successfully.');
        this.query.next(this.query.value);
      }),
      catchError((error) => {
        this.toastsService.showDanger('Error boosting user: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userDeboostClicked(user: User) {
    this.usersService.deboostUser(user).pipe(
      tap((result) => {
        this.toastsService.showSuccess('Deboost user successfully.');
        this.query.next(this.query.value);
      }),
      catchError((error) => {
        this.toastsService.showDanger('Error deboosting user: ' + JSON.stringify(error));
        return of([]);
      })
    ).subscribe();
  }

  userDeleteClicked(user: User) {
    this.confirmationService.askForConfirmation(`Delete ${user.name} ${user.lastname}?`, `Are you sure you want to delete ${user.name} ${user.lastname}?`)
      .then(() => {
        this.usersService.deleteUser(user).pipe(
          tap((result) => {
            this.toastsService.showSuccess('Deleted user successfully.');
            this.query.next(this.query.value);
          }),
          catchError((error) => {
            this.toastsService.showDanger('Error deleting user: ' + JSON.stringify(error));
            return of([]);
          })
        ).subscribe();
      })
      .catch(() => {});
  }

  ngOnDestroy() {
    if (this.timezoneSubscription) {
      this.timezoneSubscription.unsubscribe();
    }
  }

}
